/**
 * React Query hooks for Rumios (in-game currency)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../services/api';
import { useAuthStore } from '../../../../stores/auth-store';
import type { RumioBalance, RumioPack, MysteryTier, RumioPackType } from '../types';
import { CACHE_TIMES, TIER_UNLOCK_COSTS } from '../utils/constants';

/**
 * Query keys for Rumios
 */
export const rumiosKeys = {
  all: ['rumios'] as const,
  balance: () => [...rumiosKeys.all, 'balance'] as const,
  packs: () => [...rumiosKeys.all, 'packs'] as const,
  unlocked: () => [...rumiosKeys.all, 'unlocked'] as const,
  history: () => [...rumiosKeys.all, 'history'] as const,
};

/**
 * Fetch user's Rumio balance
 * Maps old API format (super_rumios, fresh_rumios, standard_rumios) to unified balance
 */
export function useRumioBalance() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: rumiosKeys.balance(),
    queryFn: async (): Promise<RumioBalance> => {
      const response = await api.getRumioBalance();
      // API returns { success: true, data: { balance, tier_costs, admin_auto_refill } }
      const data = response.data;
      return {
        balance: data?.balance ?? 0,
        tier_costs: data?.tier_costs ?? TIER_UNLOCK_COSTS,
      };
    },
    enabled: Boolean(user),
    staleTime: CACHE_TIMES.BALANCE,
    gcTime: CACHE_TIMES.BALANCE * 2,
  });
}

/**
 * Fetch available Rumio packs for purchase
 */
export function useRumioPacks() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: rumiosKeys.packs(),
    queryFn: async () => {
      const response = await api.getRumioPacks();
      // Map API response to RumioPack type, adding optional fields
      const packs: RumioPack[] = response.packs.map((pack) => {
        // Cast to Record for flexible property access
        const p = pack as Record<string, unknown>;
        return {
          type: pack.type as RumioPack['type'],
          name: pack.name,
          rumios: pack.rumios,
          price: pack.price,
          price_formatted: pack.price_formatted,
          description: (p.description as string | undefined) || '',
          available: pack.available,
          requires_tier: pack.requires_tier,
        };
      });
      return {
        packs,
        userTier: response.user_tier,
      };
    },
    enabled: Boolean(user),
    staleTime: CACHE_TIMES.PACKS,
    gcTime: CACHE_TIMES.PACKS * 2,
  });
}

/**
 * Fetch list of products user has unlocked
 */
export function useUnlockedProducts() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: rumiosKeys.unlocked(),
    queryFn: async () => {
      const response = await api.getUnlockedProducts();
      return response.unlocked;
    },
    enabled: Boolean(user),
    staleTime: CACHE_TIMES.UNLOCKED,
    gcTime: CACHE_TIMES.UNLOCKED * 2,
  });
}

/**
 * Check if a specific product is unlocked
 */
export function useIsProductUnlocked(productCode: string, storeId: string) {
  const { data: unlocked } = useUnlockedProducts();

  return unlocked?.some(
    (u) => u.product_code === productCode && u.store_id === storeId
  ) ?? false;
}

/**
 * Fetch Rumio purchase history
 */
export function useRumioHistory() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: rumiosKeys.history(),
    queryFn: async () => {
      const response = await api.getRumioHistory();
      return response.purchases;
    },
    enabled: Boolean(user),
    staleTime: CACHE_TIMES.HISTORY,
    gcTime: CACHE_TIMES.HISTORY * 2,
  });
}

/**
 * Mutation to purchase Rumio pack
 */
export function usePurchaseRumios() {
  return useMutation({
    mutationFn: (packType: RumioPackType) => api.purchaseRumios(packType as 'super' | 'fresh' | 'standard'),
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    },
    onError: (error) => {
      console.error('Purchase failed:', error);
    },
  });
}

/**
 * Mutation to unlock a product with Rumios
 */
export function useUnlockProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      product_code: string;
      store_id: string;
      tier: MysteryTier;
    }) => api.unlockProduct(params),
    onSuccess: (data) => {
      // Update balance optimistically
      queryClient.setQueryData(rumiosKeys.balance(), (old: RumioBalance | undefined) => {
        if (!old) return old;
        return {
          ...old,
          balance: data.new_balance,
        };
      });

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: rumiosKeys.unlocked() });
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

/**
 * Get the cost to unlock a specific tier
 */
export function useTierCost(tier: MysteryTier): number {
  const { data: balance } = useRumioBalance();
  return balance?.tier_costs?.[tier] ?? TIER_UNLOCK_COSTS[tier];
}

/**
 * Check if user can unlock a product based on balance and tier cost
 */
export function useCanUnlock(tier: MysteryTier | null) {
  const { data: balance } = useRumioBalance();

  if (!tier || !balance) return false;

  const cost = balance.tier_costs?.[tier] ?? TIER_UNLOCK_COSTS[tier];
  return balance.balance >= cost;
}

/**
 * Get total Rumio balance
 */
export function useTotalRumios() {
  const { data: balance } = useRumioBalance();
  return balance?.balance ?? 0;
}

/**
 * Get display info for Rumio balance
 */
export function useRumioDisplayInfo() {
  const { data: balance, isLoading, error } = useRumioBalance();

  const defaultBalance: RumioBalance = {
    balance: 0,
    tier_costs: TIER_UNLOCK_COSTS,
  };

  return {
    balance: balance ?? defaultBalance,
    isLoading,
    error,
    hasAny: (balance?.balance ?? 0) > 0,
    display: {
      balance: `${balance?.balance ?? 0}`,
      tierCosts: balance?.tier_costs ?? TIER_UNLOCK_COSTS,
    },
  };
}
